import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { store } from './redux/store';
import { getCurrentUser } from './redux/slices/authSlice';
import theme from './styles/theme';
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Missions from './pages/Missions';
import MissionDetail from './pages/MissionDetail';
import Professionals from './pages/Professionals';
import ProfessionalProfile from './pages/ProfessionalProfile';
import Organisations from './pages/Organisations';
import OrganisationProfile from './pages/OrganisationProfile';
import OrganisationPresentation from './pages/OrganisationPresentation';
import Contact from './pages/Contact';
import Register from './pages/Register';
import Login from './pages/Login';
import Blog from './pages/Blog';
import BlogDetail from './pages/BlogDetail';
import HelpCenter from './pages/HelpCenter';
import About from './pages/About';
import TermsPage from './pages/TermsPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import MentionsLegales from './pages/MentionsLegales';
import Cookies from './pages/Cookies';
import RefundPolicy from './pages/RefundPolicy';
import GuideUtilisation from './pages/GuideUtilisation';
import DashboardPro from './pages/DashboardPro';
import DashboardOrg from './pages/DashboardOrg';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import Messages from './pages/Messages';
import AuthTest from './pages/AuthTest';
import Analytics from './pages/Analytics';
import ResetPassword from './pages/ResetPassword';
import authService from './utils/authService';
import './App.css';
import VerifyEmail from './pages/VerifyEmail';

// Composant interne pour gérer l'initialisation
function AppContent() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Vérifier si l'utilisateur est connecté au démarrage
    const initializeAuth = async () => {
      const token = authService.getToken();
      const user = authService.getCurrentUser();
      
      if (token && user) {
        try {
          // Vérifier que le token est toujours valide en récupérant les infos utilisateur
          const response = await authService.getMe();
          // Si la requête réussit, mettre à jour le store Redux
          dispatch(getCurrentUser());
        } catch (error) {
          console.error('Token invalide, déconnexion:', error);
          // Le token est invalide, nettoyer le localStorage
          authService.logout();
        }
      }
    };

    initializeAuth();
  }, [dispatch]);

  return (
    <Router>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Header />
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Routes>
            <Route path="/" element={<Home />} />
            {/* Mission routes - Protected */}
            <Route path="/missions" element={<ProtectedRoute><Missions /></ProtectedRoute>} />
            <Route path="/missions/:id" element={<ProtectedRoute><MissionDetail /></ProtectedRoute>} />
            <Route path="/professionals" element={<Professionals />} />
            <Route path="/professional/:id" element={<ProfessionalProfile />} />
            <Route path="/organisations" element={<Organisations />} />
            <Route path="/organisations/:id" element={<OrganisationProfile />} />
            <Route path="/pour-les-organisations" element={<OrganisationPresentation />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPro /></ProtectedRoute>} />
            <Route path="/dashboard/pro" element={<ProtectedRoute><DashboardPro /></ProtectedRoute>} />
            <Route path="/dashboard/professionnel" element={<ProtectedRoute><DashboardPro /></ProtectedRoute>} />
            <Route path="/dashboard/org" element={<ProtectedRoute><DashboardOrg /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/modifier-profil" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/auth-test" element={<AuthTest />} />
            <Route path="/admin" element={<div>Admin Panel - Coming Soon</div>} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:id" element={<BlogDetail />} />
            <Route path="/aide" element={<HelpCenter />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/legal" element={<MentionsLegales />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/guide-utilisation" element={<GuideUtilisation />} />
            <Route path="/user-guide" element={<GuideUtilisation />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="*" element={<div>404 - Page Not Found</div>} />
          </Routes>
        </Box>
        <Footer />
      </Box>
    </Router>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppContent />
        </ThemeProvider>
      </Provider>
    </HelmetProvider>
  );
}

export default App;
